import React, {useCallback, useMemo} from 'react';
import './token-redeemed-info.scss';
import {IconBtn, LoadDocBtn} from '@ru-digital-assets/ui-component';
import currencyFormat from 'utils/currencyFormat';
import {getFormatedDate} from 'utils';
import {SIGN_DOC_TYPE} from 'types';
import {getUcpDoc} from 'store/actions/operations-sign';
import {downloadFile, getPdfFromBase64} from 'utils/files/downloadFile';
import {TokenRedemptionDocumentType, TokenRedemptionType} from 'constants/redemption';
import {useHistory} from 'react-router';
import {useDispatch, useSelector} from 'hooks/redux';
import {setPush} from 'store/actions/app';
import RedemptionApi from 'services/api/RedemptionApi';
import {hideLoader, showLoader} from 'store/actions/loader';
import {TokenStatusBadge} from 'components';

interface ITokenRedeemedInfo {
  asset: any;
  redemptionType: TokenRedemptionType;
}

export default function TokenRedeemedInfo({
  asset,
  redemptionType,
}: ITokenRedeemedInfo) {
  const dispatch = useDispatch();
  const history = useHistory();
  const token = useSelector((state) => state.auth.account.token);
  const assetId = history.location.state.id;

  const downloadDoc = useCallback(
    (id: number, type: SIGN_DOC_TYPE, title: string) => {
      dispatch(
        getUcpDoc({
          id,
          type,
          contentType: 'application/pdf',
        })
      )
        .then((res) => {
          const resultBlob = getPdfFromBase64(
            (res.payload as { doc: string; hash: string }).doc
          );
          downloadFile(resultBlob, title, 'application/pdf');
        })
        .catch(() =>
          dispatch(
            setPush({
              message: 'Ошибка при загрузке файла',
              type: 'error',
            })
          )
        );
    },
    []
  );

  const downloadPaymentDoc = useCallback((name: string, mimeType: string) => {
    dispatch(showLoader());
    RedemptionApi.getRedeemPaymentDocument(token, assetId)
      .then((response) => {
        downloadFile(response.data, name, mimeType);
      })
      .catch(() => {
        dispatch(
          setPush({
            message: 'Ошибка при загрузке документа',
            type: 'error',
          })
        );
      })
      .finally(() => {
        dispatch(hideLoader());
      });
  }, []);

  const downloadNotifyDoc = useCallback((name: string, mimeType: string, isSignature: boolean) => {
    dispatch(showLoader());
    RedemptionApi.getRedeemDocument(token, assetId, isSignature ?
        TokenRedemptionDocumentType.NOTIFY_REDEEM_DOC_SIG :
        TokenRedemptionDocumentType.NOTIFY_REDEEM_DOC)
      .then((response) => {
        downloadFile(response.data, name, mimeType);
      })
      .catch(() => {
        dispatch(
          setPush({
            message: 'Ошибка при загрузке документа',
            type: 'error',
          })
        );
      })
      .finally(() => {
        dispatch(hideLoader());
      });
  }, []);

  const downloadAgreementDoc = useCallback((name: string, mimeType: string, isSignature: boolean) => {
    dispatch(showLoader());
    RedemptionApi.getRedeemDocument(token, assetId, isSignature ?
        TokenRedemptionDocumentType.AGREEMENT_DOC_SIG :
        TokenRedemptionDocumentType.AGREEMENT_DOC)
        .then((response) => {
          downloadFile(response.data, name, mimeType);
        })
        .catch(() => {
          dispatch(
              setPush({
                message: 'Ошибка при загрузке документа',
                type: 'error',
              })
          );
        })
        .finally(() => {
          dispatch(hideLoader());
        });
  }, []);

  const decisionDoc = useMemo(() => {
    return asset.documentLinks.find((doc: any) => doc.type === 'DECISION');
  }, [asset]);

  const signedDecisionDoc = useMemo(() => {
    return asset.documentLinks.find(
      (doc: any) => doc.type === 'SIGNED_DECISION'
    );
  }, [asset]);

  return (
    <div className="token-redeemed-info">
      <div className="token-redeemed-info__token-info">
        <div className="token-redeemed-info__token-info-list">
          <div>
            <p className="p p_bold mb_8">Наименование токена:</p>
            <p className="p p_font-size_24 p_font-weight_light p_color_light-blue">
              {asset.issuingInfo.tokenName}
            </p>
          </div>
          <div>
            <p className="p p_bold mb_8">Количество (шт):</p>
            <p className="p p_font-size_24 p_font-weight_light">
              {asset.actualCount}
            </p>
          </div>
          <div>
            <p className="p p_bold mb_8">Общая стоимость:</p>
            <p className="p p_font-size_24 p_font-weight_light">
              {currencyFormat(
                asset.actualCount * asset.issuingInfo.tokenPrice,
                true
              )}
            </p>
          </div>
        </div>
        <div className="token-redeemed-info__alert-badge mt_8">
          <TokenStatusBadge text="Погашен" />
        </div>
      </div>
      <div className="token-redeemed-info__operation-details">
        <div>
          <p className="p p_font-size_12 p_color_light-grey">Эмитент:</p>
          <p className="p p_font-size_18 mb_6">{asset.issuer}</p>
          <p className="p p_font-size_12 p_color_light-grey">
            ФИО ответственного (эмитент):
          </p>
          <p className="p p_font-size_18">{asset.senderValidator}</p>
        </div>
        <div>
          <p className="p p_font-size_12 p_color_light-grey">
            Текущий Обладатель:
          </p>
          <p className="p p_font-size_18 mb_6">{asset.owner}</p>
          <p className="p p_font-size_12 p_color_light-grey">
            ФИО ответственного (Обладатель):
          </p>
          <p className="p p_font-size_18">{asset.recipientValidator}</p>
        </div>
        <div>
          <p className="p p_font-size_12 p_color_light-grey">Дата выпуска:</p>
          <p className="p p_font-size_18 mb_6">
            {getFormatedDate(asset.issuingInfo.initialDate, 'dd MMMM yyyy')}
          </p>
          <p className="p p_font-size_12 p_color_light-grey">
            Дата исполнения обязательств:
          </p>
          <p className="p p_font-size_18">
            {getFormatedDate(asset.issuingInfo.finalizedDate, 'dd MMMM yyyy')}
          </p>
        </div>
        <div>
          <p className="p p_font-size_12 p_color_light-grey">
            Основание погашения:
          </p>
          <p className="p p_font-size_18 mb_6">Исполнение обязательств</p>
          <p className="p p_font-size_12 p_color_light-grey">Дата погашения:</p>
          <p className="p p_font-size_18">
            {getFormatedDate(asset.issuingInfo.finalizedDate, 'dd MMMM yyyy')}
          </p>
        </div>
      </div>
      <div className="token-redeemed-info__documents">
        <div className="mb_32">
            <IconBtn
                title="Проверить транзакцию в блокчейн Explorer"
                sm
                outlined
                onClick={() => {
                    window!.open(`https://explorer.cfa.digital/tx/${asset.blockchainTransactionId}`, '_blank')!.focus();
                }}
            />
            {/*<BlockchainTransactionInfo hash={asset.blockchainTransactionId} />*/}
        </div>
        <p className="p p_bold mb_24">Документы по выпуску:</p>
        <div className="mb_32">
          <LoadDocBtn
            title="Решение о выпуске"
            extension={decisionDoc ? '.PDF' : undefined}
            onSpecDownload={
              decisionDoc
                ? () =>
                    downloadDoc(
                      decisionDoc.docId,
                      decisionDoc.type,
                      decisionDoc.name
                    )
                : undefined
            }
            secondExtension={signedDecisionDoc ? '.SIG' : undefined}
            onSecondSpecDownload={
              signedDecisionDoc
                ? () =>
                    downloadDoc(
                      signedDecisionDoc.docId,
                      signedDecisionDoc.type,
                      signedDecisionDoc.name
                    )
                : undefined
            }
          />
        </div>
        <p className="p p_bold mb_24">Документы для погашения:</p>
        <div className="mb_16">
          <LoadDocBtn
            title="Документ подтверждающий факт оплаты"
            extension=".PDF"
            onSpecDownload={() =>
              downloadPaymentDoc(
                'Документ подтверждающий факт оплаты',
                'application/pdf'
              )
            }
          />
        </div>
        <div className="mb_16">
          <LoadDocBtn
            title={`Уведомление об исполнении обязательств`}
            extension=".PDF"
            onSpecDownload={() =>
              downloadNotifyDoc('Уведомление об исполнении обязательств',
                'application/pdf',
                  false
              )
            }
            sig={['REDEEMED', 'REDEEM_PAYMENT_SIGNED', 'OWNER_PAYMENT_SIGNED'].includes(asset.status)}
            onSpecDownloadSig={() => {
              downloadNotifyDoc('Уведомление об исполнении обязательств.pdf.sig',
                  'application/pdf',
                  true
              )
            }}
          />
        </div>
          {['REDEEMED', 'REDEEM_PAYMENT_SIGNED', 'OWNER_PAYMENT_SIGNED'].includes(asset.status) && (
          <div className="mb_16">
              <LoadDocBtn
                  title={`Заявление о надлежащем исполнении`}
                  extension=".PDF"
                  onSpecDownload={() =>
                      downloadAgreementDoc('Заявление о надлежащем исполнении',
                          'application/pdf',
                          false
                      )
                  }
                  sig={['REDEEMED', 'OWNER_PAYMENT_SIGNED'].includes(asset.status)}
                  onSpecDownloadSig={() => {
                      downloadAgreementDoc('Заявление о надлежащем исполнении.pdf.sig',
                          'application/pdf',
                          true
                      )
                  }}
              />
          </div>
          )}
        {/*<LoadDocBtn*/}
        {/*  title={*/}
        {/*    redemptionType === TokenRedemptionType.execution*/}
        {/*      ? 'Заявление о надлежащем исполнении обязательств Эмитентом'*/}
        {/*      : 'Заявление о признании обязательств Эмитента прекратившимися'*/}
        {/*  }*/}
        {/*  extension=".PDF"*/}
        {/*  onSpecDownload={() =>*/}
        {/*    downloadRedeemDoc(*/}
        {/*      redemptionType === TokenRedemptionType.execution*/}
        {/*        ? 'Заявление о надлежащем исполнении обязательств Эмитентом'*/}
        {/*        : 'Заявление о признании обязательств Эмитента прекратившимися',*/}
        {/*      'application/pdf'*/}
        {/*    )*/}
        {/*  }*/}
        {/*/>*/}
      </div>
    </div>
  );
}
